// src/pages/DnsManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Globe, Plus, Edit2, Trash2, RefreshCw, Search, X, Save, ChevronRight, ChevronDown, Zap, Server, Settings, FileCode2, CheckCircle2 } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA', 'PTR'];

function ZoneModal({ zone, clients, settings, onClose, onSaved }) {
  const isEdit = !!zone?.id;
  const [f, setF] = useState(isEdit ? { ...zone } : { domain: '', provider: '', nameservers: '', ns1: settings?.ns1 || '', ns2: settings?.ns2 || '', hostmaster: settings?.hostmaster || '', ip: settings?.serverIp || '', createDefaults: true, notes: '', client_id: '', status: 'active' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.domain) return toast.error('Domeniu obligatoriu');
    setLoading(true);
    try {
      const { data } = isEdit
        ? await api.put(`/dns/zones/${zone.id}`, f)
        : await api.post('/dns/zones', { ...f, name: f.domain });
      toast.success(isEdit ? 'Zonă actualizată' : 'Zonă creată');
      if (data.applied === false && data.warning) toast.warning(data.warning);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return createPortal((
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header"><span className="modal-title"><Globe size={16} /> {isEdit ? zone.domain : 'Zonă DNS Nouă'}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Domeniu *</label><input className="form-input" value={f.domain} onChange={e => set('domain', e.target.value)} placeholder="example.com" disabled={isEdit} /></div>
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Provider DNS</label><input className="form-input" value={f.provider || ''} onChange={e => set('provider', e.target.value)} placeholder="Cloudflare, Route53..." /></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Activ</option><option value="inactive">Inactiv</option><option value="pending">Pending</option>
              </select></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Nameservere</label><input className="form-input" value={f.nameservers || ''} onChange={e => set('nameservers', e.target.value)} placeholder="ns1.example.com, ns2.example.com" /></div>
            {!isEdit && <>
              <div className="form-group"><label className="form-label">Server de nume 1</label><input className="form-input" value={f.ns1 || ''} onChange={e => set('ns1', e.target.value)} placeholder="ns1.exemplu.ro" /></div>
              <div className="form-group"><label className="form-label">Server de nume 2</label><input className="form-input" value={f.ns2 || ''} onChange={e => set('ns2', e.target.value)} placeholder="ns2.exemplu.ro" /></div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">E-mail administrator SOA</label><input className="form-input" type="email" value={f.hostmaster || ''} onChange={e => set('hostmaster', e.target.value)} placeholder={`hostmaster@${f.domain || 'exemplu.ro'}`} /></div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">IP server (pentru glue și înregistrările de bază)</label><input className="form-input" value={f.ip || ''} onChange={e => set('ip', e.target.value)} placeholder="203.0.113.10" /></div>
              <label className="form-label" style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={f.createDefaults} onChange={e => set('createDefaults', e.target.checked)} /> Creează automat înregistrările NS, A pentru @ și CNAME pentru www</label>
            </>}
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Client</label>
              <select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Fără client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}
              </select></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Creare'}</>}</button></div>
      </div>
    </div>
  ), document.body);
}

function RecordModal({ record, zoneId, onClose, onSaved }) {
  const isEdit = !!record?.id;
  const [f, setF] = useState(isEdit ? { ...record, value: record.value ?? record.content } : { type: 'A', name: '@', value: '', ttl: '', priority: '10', weight: '5', port: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.value) return toast.error('Valoarea este obligatorie');
    setLoading(true);
    try {
      const result = isEdit ? await api.put(`/dns/zones/${zoneId}/records/${record.id}`, f) : await api.post(`/dns/zones/${zoneId}/records`, f);
      toast.success(isEdit ? 'Înregistrare actualizată' : 'Înregistrare adăugată');
      if (result.data.applied === false && result.data.warning) toast.warning(result.data.warning);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return createPortal((
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-md">
        <div className="modal-header"><span className="modal-title">{isEdit ? 'Editare Înregistrare' : 'Înregistrare Nouă'}</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Tip</label>
              <select className="form-select" value={f.type} onChange={e => set('type', e.target.value)}>
                {RECORD_TYPES.map(t => <option key={t}>{t}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Nume</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="@ sau subdomain" /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">{f.type === 'A' ? 'Adresă IPv4' : f.type === 'AAAA' ? 'Adresă IPv6' : f.type === 'MX' ? 'Server de e-mail' : f.type === 'TXT' ? 'Text' : 'Valoare'} *</label>{f.type === 'TXT' ? <textarea className="form-textarea" rows={3} value={f.value} onChange={e => set('value', e.target.value)} placeholder="v=spf1 mx -all" /> : <input className="form-input" value={f.value} onChange={e => set('value', e.target.value)} placeholder={f.type === 'A' ? '203.0.113.10' : f.type === 'AAAA' ? '2001:db8::10' : 'hostname sau text'} />}</div>
            <div className="form-group"><label className="form-label">TTL (secunde, opțional)</label><input className="form-input" type="number" min="30" max="2592000" value={f.ttl || ''} onChange={e => set('ttl', e.target.value)} placeholder="Implicit din zona" /></div>
            {(f.type === 'MX' || f.type === 'SRV') && <div className="form-group"><label className="form-label">Prioritate</label><input className="form-input" type="number" min="0" max="65535" value={f.priority || ''} onChange={e => set('priority', e.target.value)} /></div>}
            {f.type === 'SRV' && <><div className="form-group"><label className="form-label">Pondere</label><input className="form-input" type="number" min="0" max="65535" value={f.weight || ''} onChange={e => set('weight', e.target.value)} /></div><div className="form-group"><label className="form-label">Port</label><input className="form-input" type="number" min="1" max="65535" value={f.port || ''} onChange={e => set('port', e.target.value)} /></div></>}
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Notițe</label><input className="form-input" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Adăugare'}</>}</button></div>
      </div>
    </div>
  ), document.body);
}

function ZoneDetails({ zone, onUpdated }) {
  const [records, setRecords] = useState(zone.records || []);
  const [zoneInfo, setZoneInfo] = useState(zone);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState('records');
  const [filter, setFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [zoneText, setZoneText] = useState('');
  const [soa, setSoa] = useState({ primaryNs: zone.primary_ns || zone.primaryNs || '', hostmaster: zone.hostmaster || '', ttl: zone.ttl || 3600, refresh: zone.refresh || 7200, retry: zone.retry || 3600, expire: zone.expire || 1209600, minimum: zone.minimum || 3600 });
  const [lookup, setLookup] = useState(null);
  const [lookupType, setLookupType] = useState('A');
  const [lookupLoading, setLookupLoading] = useState(false);
  const toast = useToast();
  const { hasRole } = useAuth();

  const loadRecords = async () => {
    try {
      const [recordResponse, zoneResponse] = await Promise.all([api.get(`/dns/zones/${zone.id}/records`), api.get(`/dns/zones/${zone.id}`)]);
      setRecords(recordResponse.data); setZoneInfo(zoneResponse.data);
    }
    catch (e) { toast.error('Eroare la înregistrări'); }
  };

  const delRecord = async (id) => {
    if (!window.confirm('Ștergi înregistrarea?')) return;
    try { const { data } = await api.delete(`/dns/zones/${zone.id}/records/${id}`); toast.success('Înregistrare ștearsă'); if (data.applied === false && data.warning) toast.warning(data.warning); loadRecords(); }
    catch (e) { toast.error('Eroare'); }
  };

  const doLookup = async () => {
    setLookupLoading(true);
    try { const { data } = await api.get('/dns/lookup', { params: { domain: zone.domain, type: lookupType } }); setLookup(data); }
    catch (e) { toast.error('Eroare lookup'); }
    finally { setLookupLoading(false); }
  };

  const loadZoneFile = async () => {
    try { const { data } = await api.get(`/dns/zones/${zone.id}/zone`); setZoneText(data.zone); }
    catch (e) { toast.error('Nu am putut genera fișierul de zonă'); }
  };
  const saveSoa = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/dns/zones/${zone.id}`, soa);
      setZoneInfo(p => ({ ...p, primary_ns: soa.primaryNs, primaryNs: soa.primaryNs, ...soa, serial: data.serial }));
      toast.success('Parametrii SOA au fost salvați');
      if (data.applied === false && data.warning) toast.warning(data.warning);
      onUpdated();
    } catch (error) { toast.error(error.response?.data?.error || 'Eroare la salvarea parametrilor SOA'); }
  };

  const counts = records.reduce((all, record) => ({ ...all, [record.type]: (all[record.type] || 0) + 1 }), {});
  const visibleRecords = records.filter(r => (filter === 'ALL' || r.type === filter) && (!query || `${r.name}.${zone.domain} ${r.value}`.toLowerCase().includes(query.toLowerCase())));

  const TYPE_COLORS = { A: '#4f8ef7', AAAA: '#8b5cf6', CNAME: '#06b6d4', MX: '#f59e0b', TXT: '#22c55e', NS: '#ef4444', SRV: '#ec4899', CAA: '#84cc16', PTR: '#f97316', SOA: '#14b8a6' };

  return (
    <div style={{ marginTop: 16 }}>
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[['records', `Înregistrări (${records.length})`], ['soa', 'Parametri SOA'], ['zone', 'Fișier zonă']].map(([id, label]) => <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => { setTab(id); if (id === 'zone') loadZoneFile(); }}>{label}</button>)}
      </div>
      {tab === 'soa' && <form className="form-grid form-grid-2" onSubmit={saveSoa} style={{ maxWidth: 760, marginBottom: 20 }}>
        <div className="form-group"><label className="form-label">Server de nume principal</label><input className="form-input" value={soa.primaryNs} onChange={e => setSoa(p => ({ ...p, primaryNs: e.target.value }))} required /></div>
        <div className="form-group"><label className="form-label">E-mail administrator SOA</label><input className="form-input" type="email" value={soa.hostmaster} onChange={e => setSoa(p => ({ ...p, hostmaster: e.target.value }))} required /></div>
        {['ttl','refresh','retry','expire','minimum'].map(key => <div className="form-group" key={key}><label className="form-label">{({ttl:'TTL implicit',refresh:'Refresh',retry:'Retry',expire:'Expire',minimum:'TTL negativ'})[key]} (secunde)</label><input className="form-input" type="number" min="30" value={soa[key]} onChange={e => setSoa(p => ({ ...p, [key]: e.target.value }))} required /></div>)}
        <div style={{ gridColumn: '1/-1' }}><button className="btn btn-primary" type="submit"><Save size={14} /> Salvează parametrii SOA</button><span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-secondary)' }}>Serial curent: {zoneInfo.serial}</span></div>
      </form>}
      {tab === 'zone' && <div className="card" style={{ marginBottom: 18 }}><div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}><strong>Previzualizare fișier BIND</strong><button className="btn btn-secondary" onClick={loadZoneFile}><RefreshCw size={14} /> Reîmprospătează</button></div><pre style={{ whiteSpace:'pre-wrap',overflowX:'auto',fontFamily:'var(--font-mono)',fontSize:12,lineHeight:1.7,color:'var(--text-primary)' }}>{zoneText || 'Se generează fișierul de zonă…'}</pre></div>}
      {/* Lookup tool */}
      <div style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Globe size={14} color="var(--accent)" />
        <span style={{ fontSize: 13, fontWeight: 600 }}>DNS Lookup</span>
        <select className="form-select" style={{ width: 90 }} value={lookupType} onChange={e => setLookupType(e.target.value)}>
          {RECORD_TYPES.slice(0, 6).map(t => <option key={t}>{t}</option>)}
        </select>
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={doLookup} disabled={lookupLoading}>
          {lookupLoading ? <span className="spinner" /> : <><Zap size={13} /> Lookup</>}
        </button>
        {lookup && (
          <div style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
            {lookup.result.length > 0 ? lookup.result.join(', ') : 'Niciun rezultat'}
          </div>
        )}
      </div>

      {tab === 'records' && <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Înregistrări DNS ({records.length})</span>
        <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => setModal({ new: true })}><Plus size={13} /> Adaugă</button>
      </div>
      <div style={{ display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:12 }}>
        <div style={{ position:'relative',flex:'1 1 220px' }}><Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}} /><input className="form-input" style={{paddingLeft:32}} placeholder="Caută nume sau valoare" value={query} onChange={e => setQuery(e.target.value)} /></div>
        <button className={`btn ${filter === 'ALL' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter('ALL')}>Toate ({records.length})</button>
        {RECORD_TYPES.filter(t => counts[t]).map(t => <button key={t} className={`btn ${filter === t ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(t)}>{t} ({counts[t]})</button>)}
      </div>
      <div className="table-wrapper">
        <table>
          <thead><tr><th>TIP</th><th>NUME</th><th>VALOARE</th><th>TTL</th><th>PRIORITATE</th><th>ACȚIUNI</th></tr></thead>
          <tbody>
            {records.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>Nicio înregistrare</td></tr>}
            {visibleRecords.map(r => (
              <tr key={r.id}>
                <td><span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 6, background: `${TYPE_COLORS[r.type] || '#666'}22`, color: TYPE_COLORS[r.type] || '#666' }}>{r.type}</span></td>
                <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{r.name}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.value}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.ttl}</td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.priority || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-icon" onClick={() => setModal(r)}><Edit2 size={13} /></button>
                    {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => delRecord(r.id)}><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
      {modal && <RecordModal record={modal.new ? null : modal} zoneId={zone.id} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadRecords(); }} />}
    </div>
  );
}

function DnsSettingsModal({ settings, onClose, onSaved }) {
  const [form, setForm] = useState({ ...settings });
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/dns/settings', form);
      toast.success('Setările DNS au fost salvate');
      if (data.applied === false && data.warning) toast.warning(data.warning);
      onSaved();
    } catch (error) { toast.error(error.response?.data?.error || 'Nu am putut salva setările DNS'); }
    finally { setSaving(false); }
  };
  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header"><span className="modal-title"><Settings size={16} /> Setări DNS implicite</span><button className="btn-icon" onClick={onClose}><X size={16} /></button></div>
        <div className="modal-body">
          <p style={{ color:'var(--text-secondary)',fontSize:13,marginBottom:16 }}>Valorile de mai jos sunt folosite la crearea domeniilor noi. Parametrii SOA ai domeniilor existente se modifică din detaliile fiecărei zone.</p>
          <div className="form-grid form-grid-2">
            {[["ns1","Server de nume 1","ns1.exemplu.ro"],["ns2","Server de nume 2","ns2.exemplu.ro"],["serverIp","IP-ul serverului","203.0.113.10"],["hostmaster","E-mail administrator","hostmaster@exemplu.ro"],["secondaryIps","Servere DNS secundare (IP-uri)","198.51.100.7, 198.51.100.8"]].map(([key,label,placeholder])=><div key={key} className="form-group" style={key==='secondaryIps'?{gridColumn:'1/-1'}:{}}><label className="form-label">{label}</label><input className="form-input" value={form[key] || ''} onChange={e=>set(key,e.target.value)} placeholder={placeholder} /></div>)}
            {['ttl','refresh','retry','expire','minimum'].map(key=><div key={key} className="form-group"><label className="form-label">{({ttl:'TTL implicit',refresh:'Refresh',retry:'Retry',expire:'Expire',minimum:'TTL negativ'})[key]} (secunde)</label><input className="form-input" type="number" min="30" value={form[key]} onChange={e=>set(key,e.target.value)} /></div>)}
          </div>
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Anulare</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Se salvează…':'Salvează setările'}</button></div>
      </div>
    </div>, document.body);
}

export default function DnsManager() {
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [bindStatus, setBindStatus] = useState(null);
  const [dnsSettings, setDnsSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [serverBusy, setServerBusy] = useState(false);
  const [serverLogs, setServerLogs] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [z, c, d, s, b] = await Promise.all([api.get('/dns/zones', { params: { search: search || undefined } }), api.get('/clients'), api.get('/dns/dashboard'), api.get('/dns/settings'), api.get('/dns/server/status')]);
      setZones(z.data); setClients(c.data); setDashboard(d.data); setDnsSettings(s.data.settings); setBindStatus(b.data.status);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (zoneId) => {
    if (expanded[zoneId]) { setExpanded(p => ({ ...p, [zoneId]: null })); return; }
    try {
      const { data } = await api.get(`/dns/zones/${zoneId}`);
      setExpanded(p => ({ ...p, [zoneId]: data }));
    } catch (e) { toast.error('Eroare'); }
  };

  const del = async (id, domain) => {
    if (!window.confirm(`Ștergi zona DNS pentru "${domain}"? Toate înregistrările vor fi șterse.`)) return;
    try { const { data } = await api.delete(`/dns/zones/${id}`, { data: { confirm: domain } }); toast.success('Zonă ștearsă'); if (data.applied === false && data.warning) toast.warning(data.warning); load(); }
    catch (e) { toast.error('Eroare'); }
  };

  const runServerAction = async (action) => {
    const confirmText = { stop: 'Oprești serverul DNS? Domeniile găzduite pe acest server nu vor mai răspunde.', restart: 'Repornești serverul DNS? Va fi indisponibil pentru scurt timp.' }[action];
    if (confirmText && !window.confirm(confirmText)) return;
    setServerBusy(true);
    try { const { data } = await api.post(`/dns/server/${action}`); setBindStatus(data.status); toast.success(data.message); }
    catch (e) { toast.error(e.response?.data?.error || 'Comanda către serverul DNS a eșuat'); }
    finally { setServerBusy(false); }
  };

  const loadServerLogs = async () => {
    try { const { data } = await api.get('/dns/server/logs'); setServerLogs(data); }
    catch (e) { toast.error('Nu am putut încărca jurnalul BIND'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">DNS Manager</div><div className="page-subtitle">Domenii, înregistrări DNS și server BIND</div></div>
        <div style={{ display:'flex',gap:8 }}><button className="btn btn-secondary" onClick={() => setShowSettings(true)}><Settings size={14} /> Setări DNS</button><button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Domeniu nou</button></div>
      </div>

      <div className="stats-grid" style={{ marginBottom:18 }}>
        <div className="stat-card"><div className="stat-value">{dashboard?.counts.zones ?? zones.length}</div><div className="stat-label">Domenii gestionate</div></div>
        <div className="stat-card"><div className="stat-value">{dashboard?.counts.records ?? 0}</div><div className="stat-label">Înregistrări DNS</div></div>
        <div className="stat-card"><div className="stat-value" style={{display:'flex',alignItems:'center',gap:10,fontSize:20}}><span style={{width:10,height:10,borderRadius:'50%',background:bindStatus?.running?'var(--success)':'var(--danger)'}} />{bindStatus?.running?'Activ':'Oprit'}</div><div className="stat-label">Server BIND · {bindStatus?.version || bindStatus?.service || 'status indisponibil'}</div></div>
      </div>

      <div className="card" style={{ marginBottom:18 }}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}><Server size={18} color="var(--accent)"/><div><strong>Server DNS</strong><div style={{fontSize:12,color:'var(--text-secondary)'}}>{bindStatus?.rndcError ? `Control rndc: ${bindStatus.rndcError}` : `${bindStatus?.state || 'Verificare status'} · ${bindStatus?.zonesManaged ?? 0} zone`}</div></div></div>
          {hasRole('superadmin','admin') && <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>{[['start','Pornește'],['restart','Repornește'],['stop','Oprește'],['check','Verifică'],['reload','Reîncarcă']].map(([action,label])=><button key={action} className={`btn ${action==='stop'?'btn-danger':'btn-secondary'}`} disabled={serverBusy || (action==='start' && bindStatus?.running) || (['stop','restart','reload'].includes(action) && !bindStatus?.running)} onClick={()=>runServerAction(action)}>{label}</button>)}<button className="btn btn-secondary" onClick={loadServerLogs}>Jurnal</button></div>}
        </div>
        {serverLogs && <pre style={{marginTop:14,maxHeight:220,overflow:'auto',fontSize:11,whiteSpace:'pre-wrap',color:'var(--text-secondary)'}}>{serverLogs.text || 'Jurnalul este gol.'}</pre>}
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută domeniu..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {zones.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Nicio zonă DNS. Adăugați unul cu butonul de mai sus.</div>}
            {zones.map(z => (
              <div key={z.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', background: expanded[z.id] ? 'var(--bg-hover)' : 'var(--bg-card)' }}
                  onClick={() => toggleExpand(z.id)}>
                  {expanded[z.id] ? <ChevronDown size={16} color="var(--text-secondary)" /> : <ChevronRight size={16} color="var(--text-secondary)" />}
                  <Globe size={16} color="var(--accent)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{z.domain}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{z.provider || 'Provider necunoscut'} · {z.record_count} înregistrări · {z.company_name || 'Fără client'}</div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, color: z.status === 'active' ? 'var(--success)' : 'var(--text-secondary)', background: z.status === 'active' ? '#22c55e20' : 'var(--bg-hover)' }}>{z.status}</span>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    <button className="btn-icon" onClick={() => api.get(`/dns/zones/${z.id}`).then(r => setModal(r.data))}><Edit2 size={13} /></button>
                    {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(z.id, z.domain)}><Trash2 size={13} /></button>}
                  </div>
                </div>
                {expanded[z.id] && (
                  <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border)' }}>
                    <ZoneDetails zone={expanded[z.id]} onUpdated={load} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && <ZoneModal zone={modal.new ? null : modal} clients={clients} settings={dnsSettings} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {showSettings && dnsSettings && <DnsSettingsModal settings={dnsSettings} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); load(); }} />}
    </div>
  );
}
